<template>
	<label
		ref="labelRef"
		class="relative flex flex-col md:justify-center w-full md:w-[calc(20%-20px)] min-h-[56px] md:min-h-[60px] pl-0 sm:pl-4 md:pl-6 pr-0 sm:pr-4 pb-0 sm:pb-2 md:pb-0 border-b-[1px] border-gray-light md:border-0"
		@keydown="navigateInResults"
	>
		<span class="pb-1 text-dj-gray-dark text-sm font-medium">{{ $t("jobs.searchbar.search.title") }}</span>
		<input
			ref="inputRef"
			v-model="freeWord"
			class="appearance-none outline-none bg-transparent"
			@keydown="preventKeys"
			type="text"
			:placeholder="placeholder"
			spellcheck="false"
			autocomplete="false"
			autocorrect="off"
		/>
		<!-- <span class="absolute left-0">{{ selectedSuggestion }}</span> -->

		<div
			v-if="showSuggestions && freeWord"
			class="absolute left-0 top-[60px] md:top-[85px] w-[400px] max-w-[100%] md:max-w-[100vw] bg-white border border-dj-gray-light rounded-big flex flex-col z-30"
		>
			<div class="flex flex-col p-dj-20">
				<h3 class="font-medium mb-[10px]">{{ $t("mainSearchbar.search.jobs") }}</h3>
				<div v-if="jobsResult" class="max-h-[40vh] overflow-y-auto">
					<div
						@click="setSelectedSuggestionOnClick(hit.name)"
						:class="['mb-[10px] last-of-type:mb-0 cursor-pointer', navIndex === i ? 'selected' : '']"
						v-for="(hit, i) in filteredJobs"
						:key="hit.objectID"
					>
						<span
							v-if="hit.name[locale] != undefined"
							class="block text-sm"
							v-html="highlightText(hit.name[locale])"
						></span>
						<span
							v-else
							class="block text-sm"
							v-html="highlightText(hit.name[Object.keys(hit.name)[0]])"
						></span>
					</div>
				</div>
			</div>
		</div>
	</label>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { onClickOutside, useFocus } from "@vueuse/core";
import keys from "@@/enums/keys";
import { useI18n } from "vue-i18n";

export default defineComponent({
	name: "SearchbarJobsAutocomplete",
	props: {
		modelValue: {
			type: String,
			default: "",
		},
		hitsPerPage: {
			type: Number,
			default: 20,
		},
		placeholder: {
			type: String,
			default: "",
		},
	},
	emits: ["update:modelValue", "loaded", "submit"],
	setup(props, { emit }) {
		onMounted(() => {
			const query = useRoute().query;
			const keywordParam = query.keyword || query.query;
			if (keywordParam) {
				freeWord.value = keywordParam.toString();
			}
			emit("loaded");
		});

		const { locale } = useI18n();

		const filteredJobs = ref([]);

		// input v-model
		const freeWord = ref("");

		// label/input field template ref
		const labelRef = ref();
		const inputRef = ref();

		const showSuggestions = ref(false);

		// input focus state
		const { focused: inputFocused } = useFocus(inputRef);

		// algolia
		const { result: jobsResult, search: searchJobs } = useAlgoliaSearch("jobs");

		const algoliaRegionFilter = ref("facet_region:0");

		// watch input focus state
		watch(inputFocused, (focused) => {
			focused ? (showSuggestions.value = true) : null;
		});

		onMounted(() => {
			const region = useRegion.getCurrentRegion().code;
			algoliaRegionFilter.value = region == "HU" ? "facet_region:0" : "facet_region:1";
		});

		// watch input v-model
		watch(freeWord, async (value) => {
			freeWord.value = value.replace(/[<>;"']/g, ""); // xss protection
			emit("update:modelValue", freeWord.value);
			await searchJobs({
				query: freeWord.value,
				requestOptions: { hitsPerPage: props.hitsPerPage, facetFilters: [algoliaRegionFilter.value] },
			});
			navIndex.value = 0;
			setSelectedSuggestion();
		});

		watch(
			() => props.modelValue,
			(value) => {
				if (value == "") freeWord.value = value;
			}
		);

		onClickOutside(labelRef, () => {
			showSuggestions.value = false;
		});

		function preventKeys(event) {
			// prevent keys
			if (
				event.keyCode === keys.ARROW_UP ||
				event.keyCode === keys.ARROW_DOWN ||
				event.keyCode === keys.TAB ||
				event.keyCode === keys.ENTER ||
				event.keyCode === keys.ESCAPE
			)
				event.preventDefault();

			if (event.keyCode === keys.ENTER) {
				if (selectedSuggestion.value) {
					freeWord.value = selectedSuggestion.value;
				}
				emit("update:modelValue", freeWord.value);
				emit("submit");
				selectedSuggestion.value = "";
				showSuggestions.value = false;
				inputFocused.value = false;
				return;
			}

			if (event.keyCode === keys.TAB) {
				if (selectedSuggestion.value) {
					freeWord.value = selectedSuggestion.value;
				}
				selectedSuggestion.value = "";
				showSuggestions.value = false;
				inputFocused.value = false;
			}
			if (event.keyCode === keys.ESCAPE) {
				inputFocused.value = false;
				showSuggestions.value = false;
			}
		}

		// current index
		const navIndex = ref(0);

		watch(navIndex, () => {
			setSelectedSuggestion();
		});

		function navigateInResults(evt) {
			if (evt.keyCode === keys.ARROW_UP || evt.keyCode === keys.ARROW_DOWN) {
				// max value of indexes
				const maxValue = jobsResult.value.hits.length - 1;
				switch (evt.keyCode) {
					case keys.ARROW_UP:
						if (navIndex.value === 0) navIndex.value = maxValue;
						else navIndex.value--;
						break;
					case keys.ARROW_DOWN:
						if (navIndex.value === maxValue) navIndex.value = 0;
						else navIndex.value++;
						break;
				}
			}
		}

		const selectedSuggestion = ref("");

		function setSelectedSuggestion() {
			if (
				navIndex.value >= 0 &&
				navIndex.value <= filteredJobs.value.length - 1 &&
				filteredJobs.value.length !== 0
			) {
				// selected job name in current locale
				const selectedJob = filteredJobs.value[navIndex.value].name[locale.value];
				if (selectedJob != undefined) {
					selectedSuggestion.value = selectedJob;
				}
				// if name is unavailable in current locale, select the first locale key in name object
				else {
					selectedSuggestion.value =
						filteredJobs.value[navIndex.value].name[
							Object.keys(filteredJobs.value[navIndex.value].name)[0]
						];
				}
			}
		}

		function setSelectedSuggestionOnClick(suggestion: object) {
			if (suggestion[locale.value] != undefined) {
				freeWord.value = suggestion[locale.value];
			} else {
				freeWord.value = suggestion[Object.keys(suggestion)[0]];
			}
			selectedSuggestion.value = "";
			showSuggestions.value = false;
			inputFocused.value = false;
		}

		function highlightText(text: string) {
			const cleanFreeWord = freeWord.value.replace(/[^a-zA-Z0-9]/g, "");
			return text.replace(new RegExp(cleanFreeWord, "gi"), (match) => {
				return "<mark class='highlight'>" + match + "</mark>";
			});
		}

		// filter algolia results to avoid duplicated job name
		watch(jobsResult, (value) => {
			filteredJobs.value = Array.from(
				new Set(
					value.hits.map((a) => {
						if (a.name[locale.value] != undefined) {
							return a.name[locale.value];
						} else {
							return a.name[Object.keys(a.name)[0]];
						}
					})
				)
			).map((name) => {
				return value.hits.find((a) => {
					if (a.name[locale.value] != undefined) {
						return a.name[locale.value] == name;
					} else {
						return a.name[Object.keys(a.name)[0]] == name;
					}
				});
			});
			filteredJobs.value.unshift({
				name: { en: freeWord.value, hu: freeWord.value, ro: freeWord.value },
				objectID: "0",
			});
		});

		return {
			freeWord,
			preventKeys,
			jobsResult,
			highlightText,
			labelRef,
			inputRef,
			showSuggestions,
			navIndex,
			navigateInResults,
			selectedSuggestion,
			setSelectedSuggestionOnClick,
			filteredJobs,
			locale,
		};
	},
});
</script>

<style lang="scss">
.highlight {
	@apply text-dj-red font-semibold bg-transparent;
}

.selected {
	@apply text-dj-red;
}
</style>
